import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate } from "react-router";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import { ArrowLeft, ArrowRight, Check, Send, X } from "lucide-react";
import { toast } from "sonner";

import { SocialPostPreview } from "@/components/campaign/social-post-preview";
import {
  CarouselSlideRail,
  CarouselSlideSummary,
} from "@/components/campaign/carousel-slide-review";
import { buildBrandPath } from "@/hooks/use-route-brand";
import {
  canOpenCalendarPostEditor,
  getCalendarApprovalLabel,
  getCalendarPostReviewActions,
  type CalendarPostLike,
} from "@/lib/calendar-posts";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { downloadSocialPostPreviewImage } from "@/lib/social-post-preview-download";
import {
  resolveScheduledPostPreview,
  type ScheduledPostDraft,
  type ScheduledPostCarouselOutput,
  type ScheduledPostPreviewLogoAsset,
} from "@/lib/scheduled-post-preview";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { createSocialPostEditableDraft, type SocialPostEditableDraft } from "@runcheetah/shared";
import { Badge, Button } from "@runcheetah/ui";
import { PostPreviewOverflowMenu } from "./post-preview-overflow-menu";

const EDITABLE_SHORTCUT_TAG_NAMES = new Set(["input", "select", "textarea"]);

export const isEditableShortcutTarget = (target: EventTarget | null): boolean => {
  if (!target || !("tagName" in target)) {
    return false;
  }

  const element = target as { tagName: unknown; isContentEditable?: boolean };
  if (typeof element.tagName !== "string") {
    return false;
  }

  return (
    Boolean(element.isContentEditable) ||
    EDITABLE_SHORTCUT_TAG_NAMES.has(element.tagName.toLowerCase())
  );
};

export function getAdjacentCarouselSlideId({
  carousel,
  selectedSlideId,
  direction,
}: {
  carousel: ScheduledPostCarouselOutput | null;
  selectedSlideId: string | null;
  direction: "previous" | "next";
}): string | null {
  if (!carousel || carousel.slides.length <= 1) {
    return null;
  }

  const selectedIndex = selectedSlideId
    ? carousel.slides.findIndex((slideOutput) => slideOutput.slide.id === selectedSlideId)
    : 0;
  const currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
  const nextIndex = direction === "previous" ? currentIndex - 1 : currentIndex + 1;

  return carousel.slides[nextIndex]?.slide.id ?? null;
}

export type ScheduledPostOverlayPost = CalendarPostLike & {
  output?: unknown;
  draft?: ScheduledPostDraft | null;
  draftUpdatedAt?: string | null;
};

export function ScheduledPostPreviewOverlay({
  post,
  brandId,
  profileLabel,
  logoAssets,
  backLabel,
  hasPrevious,
  hasNext,
  approving,
  rejecting,
  publishing = false,
  onClose,
  onPrevious,
  onNext,
  onApprove,
  onReject,
  onPublishNow,
}: {
  post: ScheduledPostOverlayPost | null;
  brandId: string;
  profileLabel: string;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  backLabel: string;
  hasPrevious: boolean;
  hasNext: boolean;
  approving: boolean;
  rejecting: boolean;
  publishing?: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onPublishNow?: () => void;
}) {
  if (!post) return null;

  return (
    <ScheduledPostPreviewOverlayContent
      post={post}
      brandId={brandId}
      profileLabel={profileLabel}
      logoAssets={logoAssets}
      backLabel={backLabel}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      approving={approving}
      rejecting={rejecting}
      publishing={publishing}
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
      onApprove={onApprove}
      onReject={onReject}
      onPublishNow={onPublishNow}
    />
  );
}

function ScheduledPostPreviewOverlayContent({
  post,
  brandId,
  profileLabel,
  logoAssets,
  backLabel,
  hasPrevious,
  hasNext,
  approving,
  rejecting,
  publishing,
  onClose,
  onPrevious,
  onNext,
  onApprove,
  onReject,
  onPublishNow,
}: {
  post: ScheduledPostOverlayPost;
  brandId: string;
  profileLabel: string;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  backLabel: string;
  hasPrevious: boolean;
  hasNext: boolean;
  approving: boolean;
  rejecting: boolean;
  publishing: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onPublishNow?: () => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [selectedCarouselSlideId, setSelectedCarouselSlideId] = useState<string | null>(null);
  const preview = resolveScheduledPostPreview({
    output: post.output,
    draft: post.draft ?? null,
    selectedCarouselSlideId,
    logoAssets,
  });
  const actions = getCalendarPostReviewActions(post);
  const scheduledAt = new Date(post.scheduledFor);
  const scheduledDateLabel = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(scheduledAt);
  const scheduledTimeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(scheduledAt);
  const ownerLabel =
    post.owner.type === "campaign"
      ? intl.formatMessage({ id: "calendar.owner.campaign", defaultMessage: "Campaign" })
      : intl.formatMessage({ id: "calendar.owner.standalone", defaultMessage: "Standalone" });
  const ownerPath =
    post.owner.type === "campaign" ? buildBrandPath(brandId, `/campaigns/${post.owner.id}`) : null;
  const editorPath = buildFocusedPostEditorPath({
    brandId,
    owner: {
      type: post.owner.type,
      id: post.owner.id,
    },
    postId: post.id,
  });
  const statusLabel = getCalendarApprovalLabel(post, intl);
  const canEditDesign = canOpenCalendarPostEditor(post);
  const isStoryPost = preview.plan?.postType === "story";
  const savedCaption = preview.caption.trim();
  const savedHashtags = preview.hashtags;
  const savedHashtagsText = savedHashtags.join(" ");

  const [editCaption, setEditCaption] = useState(savedCaption);
  const [editHashtags, setEditHashtags] = useState(savedHashtagsText);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const sceneStageRef = useRef<KonvaStage | null>(null);

  useEffect(() => {
    setEditCaption(savedCaption);
    setEditHashtags(savedHashtagsText);
    setSaveError(null);
  }, [post.id, savedCaption, savedHashtagsText]);

  const isDirty = editCaption !== savedCaption || editHashtags !== savedHashtagsText;

  const campaignSaveMutation = trpc.campaign.upsertPostDraft.useMutation({
    onSuccess: () => {
      setSaveError(null);
      void utils.calendar.list.invalidate();
    },
    onError: (err) => setSaveError(err.message),
  });
  const standaloneSaveMutation = trpc.standaloneAssetBatch.upsertPostDraft.useMutation({
    onSuccess: () => {
      setSaveError(null);
      void utils.calendar.list.invalidate();
    },
    onError: (err) => setSaveError(err.message),
  });
  const isSaving = campaignSaveMutation.isPending || standaloneSaveMutation.isPending;
  const openFocusedEditor = () => {
    if (!canEditDesign) return;

    navigate(editorPath, { state: { backgroundLocation: location } });
  };
  const openInstagramIntegrations = () => {
    navigate(buildBrandPath(brandId, "/integrations"));
  };

  const downloadImage = async () => {
    setDownloading(true);

    try {
      await downloadSocialPostPreviewImage({
        stage: sceneStageRef.current,
        previewAssetUrl: preview.previewAsset?.signedUrl ?? null,
        fileName: `${post.owner.type}-${post.id}.png`,
      });
    } catch (caught) {
      toast.error(
        caught instanceof Error
          ? caught.message
          : intl.formatMessage({
              id: "approvals.downloadFailed",
              defaultMessage: "Could not download the image.",
            }),
      );
    } finally {
      setDownloading(false);
    }
  };

  const postMenuTrigger = (
    <PostPreviewOverflowMenu
      size="overlay"
      downloading={downloading}
      rejecting={approving || rejecting || publishing}
      onDownload={() => void downloadImage()}
      onReject={actions.canReject ? onReject : undefined}
      triggerClassName="bg-background/80 hover:bg-background"
    />
  );

  const handleSave = () => {
    if (!preview.plan || preview.carousel) return;
    const newHashtags = editHashtags
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    const baseDraft: SocialPostEditableDraft = post.draft
      ? { ...post.draft, caption: editCaption || null, hashtags: newHashtags }
      : {
          ...createSocialPostEditableDraft({ plan: preview.plan, caption: editCaption || null }),
          hashtags: newHashtags,
        };

    if (post.owner.type === "campaign") {
      campaignSaveMutation.mutate({
        brandId,
        campaignId: post.owner.id,
        scheduledPostId: post.id,
        draft: baseDraft,
        expectedDraftUpdatedAt: post.draftUpdatedAt ?? null,
      });
    } else {
      standaloneSaveMutation.mutate({
        brandId,
        batchId: post.owner.id,
        scheduledPostId: post.id,
        draft: baseDraft,
        expectedDraftUpdatedAt: post.draftUpdatedAt ?? null,
      });
    }
  };

  useEffect(() => {
    setSelectedCarouselSlideId(null);
  }, [post.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableShortcutTarget(document.activeElement ?? event.target)) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") {
        const previousSlideId = getAdjacentCarouselSlideId({
          carousel: preview.carousel,
          selectedSlideId: preview.carouselSlide?.slide.id ?? selectedCarouselSlideId,
          direction: "previous",
        });

        if (previousSlideId) {
          event.preventDefault();
          setSelectedCarouselSlideId(previousSlideId);
        }

        return;
      }
      if (event.key === "ArrowRight") {
        const nextSlideId = getAdjacentCarouselSlideId({
          carousel: preview.carousel,
          selectedSlideId: preview.carouselSlide?.slide.id ?? selectedCarouselSlideId,
          direction: "next",
        });

        if (nextSlideId) {
          event.preventDefault();
          setSelectedCarouselSlideId(nextSlideId);
        }

        return;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, preview.carousel, preview.carouselSlide?.slide.id, selectedCarouselSlideId]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background" onClick={onClose}>
      <div
        className="flex shrink-0 items-center justify-between border-b bg-background/95 px-4 py-3 backdrop-blur sm:px-6"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="-ml-1 gap-1.5 rounded-full text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="h-8 w-8 rounded-full"
              aria-label={intl.formatMessage({
                id: "calendar.previousPost",
                defaultMessage: "Previous post",
              })}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onNext}
              disabled={!hasNext}
              className="h-8 w-8 rounded-full"
              aria-label={intl.formatMessage({
                id: "calendar.nextPost",
                defaultMessage: "Next post",
              })}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {actions.canReject ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={onReject}
              disabled={approving || rejecting || publishing}
            >
              <X className="h-4 w-4" />
              {rejecting ? (
                <FormattedMessage id="calendar.rejecting" defaultMessage="Rejecting…" />
              ) : (
                <FormattedMessage id="common.reject" defaultMessage="Reject" />
              )}
            </Button>
          ) : null}
          {actions.canConnectInstagram ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={openInstagramIntegrations}
              disabled={approving || rejecting || publishing}
            >
              <img src="/instagram.svg" alt="" className="h-4 w-4" aria-hidden={true} />
              <FormattedMessage
                id="approvals.connectInstagram"
                defaultMessage="Connect Instagram"
              />
            </Button>
          ) : actions.canDownload ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={() => void downloadImage()}
              disabled={downloading || approving || rejecting || publishing}
            >
              {downloading ? (
                "…"
              ) : (
                <FormattedMessage id="approvals.downloadImage" defaultMessage="Download image" />
              )}
            </Button>
          ) : actions.canApprove ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={onApprove}
              disabled={approving || rejecting || publishing}
            >
              <Check className="h-4 w-4" />
              {approving ? (
                <FormattedMessage id="calendar.approving" defaultMessage="Approving…" />
              ) : (
                <FormattedMessage id="common.approve" defaultMessage="Approve" />
              )}
            </Button>
          ) : actions.showPublishNow ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={onPublishNow}
              disabled={!actions.canPublishNow || approving || rejecting || publishing}
            >
              <Send className="h-4 w-4" />
              {publishing || actions.publishPending ? (
                <FormattedMessage id="approvals.publishNowPending" defaultMessage="Publishing…" />
              ) : (
                <FormattedMessage id="approvals.publishNow" defaultMessage="Publish now" />
              )}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 flex-1 overflow-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
            {preview.plan ? (
              <div className="w-full max-w-[540px] space-y-4">
                <SocialPostPreview
                  renderMode="preview"
                  platform={preview.plan.platform}
                  type={preview.plan.postType}
                  plan={preview.plan}
                  linkedAssets={preview.linkedAssets}
                  brandkit={preview.brandkit}
                  profileLabel={profileLabel}
                  caption={editCaption}
                  hashtags={editHashtags
                    .split(/[\s,]+/)
                    .map((t) => t.replace(/^#/, "").trim())
                    .filter(Boolean)}
                  maxWidth={540}
                  className="w-full max-w-[540px]"
                  sceneStageRef={sceneStageRef}
                  headerAction={postMenuTrigger}
                  mediaOverlay={
                    canEditDesign ? (
                      <div className="group flex h-full w-full items-center justify-center bg-black/0 transition-[background-color,opacity] duration-200 hover:bg-black/16 focus-within:bg-black/16">
                        <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                          <Button
                            type="button"
                            className="pointer-events-auto h-20 rounded-full bg-background/96 px-12 text-2xl font-semibold text-foreground shadow-lg hover:bg-background"
                            onClick={openFocusedEditor}
                          >
                            <FormattedMessage
                              id="calendar.editDesign"
                              defaultMessage="Edit design"
                            />
                          </Button>
                        </div>
                      </div>
                    ) : undefined
                  }
                />
                {preview.carousel ? (
                  <CarouselSlideRail
                    carousel={preview.carousel}
                    selectedSlideId={preview.carouselSlide?.slide.id ?? null}
                    onSelectSlide={setSelectedCarouselSlideId}
                  />
                ) : null}
              </div>
            ) : preview.previewAsset ? (
              <div className="group relative w-full max-w-[540px] overflow-hidden rounded-xl">
                <div className="absolute right-4 top-4 z-20">{postMenuTrigger}</div>
                <img
                  src={preview.previewAsset.signedUrl}
                  alt={
                    savedCaption ||
                    intl.formatMessage({
                      id: "calendar.postPreviewAlt",
                      defaultMessage: "Post preview",
                    })
                  }
                  width={preview.previewAsset.width ?? 1080}
                  height={preview.previewAsset.height ?? 1350}
                  className="block w-full max-w-[540px] rounded-xl object-cover"
                  loading="lazy"
                />
                {canEditDesign ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/16 group-hover:opacity-100 group-focus-within:bg-black/16 group-focus-within:opacity-100">
                    <Button
                      type="button"
                      className="pointer-events-auto h-20 rounded-full bg-background/96 px-12 text-2xl font-semibold text-foreground shadow-lg hover:bg-background"
                      onClick={openFocusedEditor}
                    >
                      <FormattedMessage id="calendar.editDesign" defaultMessage="Edit design" />
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex aspect-4/5 w-full max-w-[540px] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
                <FormattedMessage id="calendar.previewPending" defaultMessage="Preview pending" />
              </div>
            )}
          </div>
        </div>

        <aside
          className={cn(
            "hidden shrink-0 overflow-auto border-l p-5 lg:flex lg:flex-col lg:gap-5",
            isStoryPost ? "w-[28rem] xl:w-[32rem]" : "w-80",
          )}
        >
          <div className={cn("space-y-4", isStoryPost ? "p-2" : "p-1")}>
            <div className="flex items-center justify-between gap-3">
              <span
                className={cn(
                  "font-medium text-muted-foreground",
                  isStoryPost ? "text-base" : "text-sm",
                )}
              >
                <FormattedMessage id="common.status" defaultMessage="Status" />
              </span>
              <Badge
                shape="pill"
                variant="outline"
                className={cn(
                  isStoryPost
                    ? "rounded-full border-transparent px-3 py-1 text-xs font-semibold"
                    : "rounded-full border-transparent px-2.5 py-0.5 text-[11px] font-semibold",
                  statusLabel === "Approved" && "bg-emerald-100 text-emerald-800",
                  statusLabel === "Needs approval" && "bg-amber-100 text-amber-900",
                  statusLabel === "Rejected" && "bg-destructive/10 text-destructive",
                  statusLabel === "Pending render" && "bg-muted text-muted-foreground",
                )}
              >
                {statusLabel}
              </Badge>
            </div>
            <div className="flex items-start justify-between gap-3">
              <span
                className={cn(
                  "font-medium text-muted-foreground",
                  isStoryPost ? "text-base" : "text-sm",
                )}
              >
                <FormattedMessage id="calendar.scheduled" defaultMessage="Scheduled" />
              </span>
              <div className="text-right">
                <p
                  className={cn(
                    "font-semibold text-foreground",
                    isStoryPost ? "text-base" : "text-sm",
                  )}
                >
                  {scheduledDateLabel}
                </p>
                <p
                  className={cn(
                    "mt-0.5 text-muted-foreground",
                    isStoryPost ? "text-sm" : "text-xs",
                  )}
                >
                  {scheduledTimeLabel}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span
                className={cn(
                  "font-medium text-muted-foreground",
                  isStoryPost ? "text-base" : "text-sm",
                )}
              >
                {ownerLabel}
              </span>
              {ownerPath ? (
                <Link
                  to={ownerPath}
                  className={cn(
                    "max-w-[60%] truncate text-right font-semibold text-foreground underline-offset-4 hover:underline",
                    isStoryPost ? "text-base" : "text-sm",
                  )}
                >
                  {post.owner.name}
                </Link>
              ) : (
                <span
                  className={cn(
                    "max-w-[60%] truncate text-right font-semibold text-foreground",
                    isStoryPost ? "text-base" : "text-sm",
                  )}
                >
                  {post.owner.name}
                </span>
              )}
            </div>
          </div>

          {preview.carousel ? (
            <>
              <CarouselSlideRail
                carousel={preview.carousel}
                selectedSlideId={preview.carouselSlide?.slide.id ?? null}
                onSelectSlide={setSelectedCarouselSlideId}
              />
              <CarouselSlideSummary slideOutput={preview.carouselSlide} compact />
            </>
          ) : null}

          <div className={cn("space-y-3", isStoryPost ? "p-2" : "p-1")}>
            <div className="flex items-center justify-between gap-2">
              <p
                className={cn(
                  "font-semibold uppercase tracking-[0.14em] text-muted-foreground",
                  isStoryPost ? "text-sm" : "text-xs",
                )}
              >
                <FormattedMessage id="calendar.caption" defaultMessage="Caption" />
              </p>
              {isDirty && (
                <span
                  className={cn(
                    "font-medium text-amber-600 uppercase tracking-wide",
                    isStoryPost ? "text-xs" : "text-[10px]",
                  )}
                >
                  <FormattedMessage id="calendar.unsaved" defaultMessage="Unsaved" />
                </span>
              )}
            </div>
            <textarea
              value={editCaption}
              onChange={(e) => setEditCaption(e.target.value)}
              rows={isStoryPost ? 12 : 9}
              placeholder={intl.formatMessage({
                id: "calendar.captionPlaceholder",
                defaultMessage: "Add a caption…",
              })}
              className={cn(
                "w-full resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring",
                isStoryPost ? "text-base leading-7" : "text-sm leading-relaxed",
              )}
            />
            <div className="space-y-1.5">
              <p
                className={cn(
                  "font-medium text-muted-foreground",
                  isStoryPost ? "text-sm" : "text-xs",
                )}
              >
                <FormattedMessage id="calendar.hashtags" defaultMessage="Hashtags" />
              </p>
              <input
                value={editHashtags}
                onChange={(e) => setEditHashtags(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "calendar.hashtagsPlaceholder",
                  defaultMessage: "#brand #product…",
                })}
                className={cn(
                  "w-full rounded-xl border border-input bg-background px-3 py-2 text-[#0b54d6] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring",
                  isStoryPost ? "text-base" : "text-sm",
                )}
              />
              <p className={cn("text-muted-foreground", isStoryPost ? "text-xs" : "text-[11px]")}>
                <FormattedMessage
                  id="calendar.hashtagsHelp"
                  defaultMessage="Space or comma separated"
                />
              </p>
            </div>
            {saveError && <p className="text-xs text-destructive">{saveError}</p>}
            <Button
              type="button"
              size="sm"
              className="w-full rounded-full"
              disabled={!isDirty || isSaving || !preview.plan || Boolean(preview.carousel)}
              onClick={handleSave}
            >
              {isSaving ? (
                <FormattedMessage id="common.saving" defaultMessage="Saving…" />
              ) : (
                <FormattedMessage id="calendar.saveCaption" defaultMessage="Save caption" />
              )}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
